import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronLeft, Download, FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody } from '../../components/layout';
import { PageHeader } from '../../components/PageHeader';
import { useProjectContext } from '../../contexts/ProjectContext';
import { documentApiConnect } from '../../lib/api/documentApi.connect';
const statusBadge = (status)=>{
    switch(status){
        case 'ready':
            return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        case 'processing':
        case 'queued':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        case 'error':
            return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
};
/**
 * Project-scoped documents view: every processed document that belongs to
 * THIS project (all members' uploads), served from the document library's
 * project scope. Raw file management stays on the Overview's Files section;
 * this page is the processed-document library slice for the project.
 */ export function ProjectDocuments() {
    const navigate = useNavigate();
    const { projectId, project, isLoading, error } = useProjectContext();
    // Offset paging in windows of 100: baltigo clamps limit to [1,100] and
    // resets anything larger to 50, so a growing limit SHRINKS the list past
    // the first page. Successive offsets are the only shape the server pages.
    const { data: docsResult, isLoading: docsLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error: docsError } = useInfiniteQuery({
        queryKey: [
            'projectDocuments',
            projectId
        ],
        enabled: !!projectId,
        initialPageParam: 0,
        // Poll only while something is actually in flight — the backing list is
        // a tenant scan, so an idle open tab must not re-run it every 5s forever.
        refetchInterval: (query)=>(query.state.data?.pages ?? []).some((p)=>p.documents.some((d)=>d.status === 'processing' || d.status === 'queued')) ? 5000 : false,
        // Known limitation: documents processed before the library learned
        // project attribution carry no project id and will not appear here —
        // the list is forward-looking; the program Docs tab (which heals) is
        // the complete record.
        queryFn: ({ pageParam })=>documentApiConnect.listDocuments({
                projectId: projectId ?? '',
                limit: 100,
                offset: pageParam
            }),
        getNextPageParam: (lastPage)=>lastPage.pagination.hasMore ? lastPage.pagination.offset + lastPage.documents.length : undefined
    });
    // Dedup across pages: a document finishing processing between page fetches
    // shifts offsets, which can repeat a row on the boundary.
    const docs = (()=>{
        if (!docsResult) return undefined;
        const seen = new Set();
        return docsResult.pages.flatMap((p)=>p.documents).filter((d)=>{
            if (seen.has(d.id)) return false;
            seen.add(d.id);
            return true;
        });
    })();
    const totalDocs = docsResult?.pages[docsResult.pages.length - 1]?.pagination.total ?? 0;
    const handleDownload = async (docId)=>{
        try {
            const result = await documentApiConnect.downloadDocument(docId);
            const url = window.URL.createObjectURL(result.blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', result.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Download failed');
        }
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-4 w-24 bg-muted rounded animate-pulse mb-2"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-32 bg-muted rounded animate-pulse"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
                        "aria-busy": "true",
                        children: [
                            1,
                            2,
                            3
                        ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                className: "h-32 bg-muted rounded-lg animate-pulse"
                            }, i))
                    })
                })
            ]
        });
    }
    if (error || !project || !projectId) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/projects",
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Projects"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Project not found"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-red-600 mb-4",
                                children: "This project doesn't exist or you don't have access."
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: ()=>navigate('/projects'),
                                children: "Back to Projects"
                            })
                        ]
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-xl font-semibold",
                            children: "Documents"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Project document library"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(PageBody, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-5xl",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mb-4",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-semibold",
                                    children: "Documents"
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: [
                                        "Processed documents from this project's uploads. Raw files live on the",
                                        ' ',
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: `/project/${projectId}`,
                                            className: "text-primary hover:underline",
                                            children: "overview"
                                        }),
                                        "; the tenant-wide library is under",
                                        ' ',
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: "/documents",
                                            className: "text-primary hover:underline",
                                            children: "global documents"
                                        }),
                                        "."
                                    ]
                                })
                            ]
                        }),
                        docsLoading ? /*#__PURE__*/ _jsx("div", {
                            className: "space-y-2",
                            "aria-busy": "true",
                            children: [
                                1,
                                2,
                                3
                            ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                    className: "h-12 bg-muted rounded animate-pulse"
                                }, i))
                        }) : docsError ? /*#__PURE__*/ _jsxs("div", {
                            className: "text-center py-12",
                            "data-testid": Testids.project.documentsList,
                            children: [
                                /*#__PURE__*/ _jsx(FileText, {
                                    className: "h-12 w-12 mx-auto mb-3 text-red-500"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-red-600 mb-2",
                                    children: "Couldn't load this project's documents"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: docsError instanceof Error ? docsError.message : 'Please try again.'
                                })
                            ]
                        }) : !docs || docs.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                            className: "text-center py-12",
                            "data-testid": Testids.project.documentsList,
                            children: [
                                /*#__PURE__*/ _jsx(FileText, {
                                    className: "h-12 w-12 mx-auto mb-3 text-muted-foreground"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-muted-foreground mb-2",
                                    children: "No documents yet"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "Upload files on the overview and process them — the results land here."
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs("div", {
                            className: "border rounded-lg divide-y divide-border overflow-hidden",
                            "data-testid": Testids.project.documentsList,
                            children: [
                                hasNextPage && /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between px-4 py-2 text-xs text-muted-foreground bg-muted/40",
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "Showing the ",
                                                docs.length,
                                                " most recent of ",
                                                totalDocs,
                                                " documents."
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            variant: "ghost",
                                            className: "h-6 text-xs",
                                            disabled: isFetchingNextPage,
                                            onClick: ()=>fetchNextPage(),
                                            children: isFetchingNextPage ? 'Loading…' : 'Load more'
                                        })
                                    ]
                                }),
                                docs.map((doc)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3 px-4 py-3 bg-background",
                                        "data-testid": Testids.project.documentsRow,
                                        children: [
                                            /*#__PURE__*/ _jsx(FileText, {
                                                className: "w-5 h-5 text-muted-foreground flex-shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-0 flex-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "text-sm font-medium truncate",
                                                        children: doc.filename
                                                    }),
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "text-xs text-muted-foreground",
                                                        children: new Date(doc.createdAt).toLocaleDateString()
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusBadge(doc.status)}`,
                                                children: doc.status
                                            }),
                                            /*#__PURE__*/ _jsx(Button, {
                                                size: "sm",
                                                variant: "ghost",
                                                onClick: ()=>handleDownload(doc.id),
                                                disabled: doc.status !== 'ready',
                                                "aria-label": `Download ${doc.filename}`,
                                                children: /*#__PURE__*/ _jsx(Download, {
                                                    className: "w-4 h-4"
                                                })
                                            })
                                        ]
                                    }, doc.id))
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
